import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';

// Renders the serialized Notion blocks produced by scripts/fetch-notion.js.
// Styling comes from the `.prose` rules in BlogPage.css.

function CodeBlock({ language, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked — nothing to do
    }
  };
  return (
    <div className="code">
      <div className="code__bar">
        {language && language !== 'plain text' && <span className="code__lang">{language}</span>}
        <button
          type="button"
          className={`code__copy${copied ? ' is-copied' : ''}`}
          onClick={copy}
          aria-label={copied ? 'Copied' : 'Copy code'}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
      <pre>
        <code>{text}</code>
      </pre>
    </div>
  );
}

function ZoomImage({ src, alt }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <>
      <img src={src} alt={alt} loading="lazy" onClick={() => setOpen(true)} />
      {open && (
        <div className="zoom" role="dialog" aria-label="Image preview" onClick={() => setOpen(false)}>
          <img src={src} alt={alt} />
        </div>
      )}
    </>
  );
}

// Clicking a heading's # copies a deep link to it.
function copyHeadingLink(e, id) {
  e.preventDefault();
  const url = `${window.location.origin}${window.location.pathname}#${id}`;
  window.history.replaceState(null, '', `#${id}`);
  navigator.clipboard?.writeText(url).catch(() => {});
  document.getElementById(id)?.scrollIntoView({ block: 'start' });
}

function RichText({ content }) {
  if (!content?.length) return null;
  return content.map((span, i) => {
    let el = span.text;
    if (span.code) el = <code>{el}</code>;
    if (span.bold) el = <strong>{el}</strong>;
    if (span.italic) el = <em>{el}</em>;
    if (span.strikethrough) el = <del>{el}</del>;
    if (span.href) {
      return (
        <a key={i} href={span.href} target="_blank" rel="noopener noreferrer">
          {el}
        </a>
      );
    }
    return <span key={i}>{el}</span>;
  });
}

const HEADING_TAG = { heading_1: 'h2', heading_2: 'h3', heading_3: 'h4' };

function renderBlock(block, key) {
  switch (block.type) {
    case 'paragraph':
      if (!block.content?.length) return null;
      return (
        <p key={key}>
          <RichText content={block.content} />
        </p>
      );

    case 'heading_1':
    case 'heading_2':
    case 'heading_3': {
      const Tag = HEADING_TAG[block.type];
      return (
        <Tag key={key} id={block.headingId} tabIndex={-1}>
          <a
            className="anchor"
            href={`#${block.headingId}`}
            onClick={(e) => copyHeadingLink(e, block.headingId)}
            aria-label="Copy link to section"
          >
            #
          </a>
          <RichText content={block.content} />
        </Tag>
      );
    }

    case 'code':
      return (
        <CodeBlock
          key={key}
          language={block.language}
          text={block.content?.map((s) => s.text).join('') || ''}
        />
      );

    case 'quote':
      return (
        <blockquote key={key}>
          <RichText content={block.content} />
        </blockquote>
      );

    case 'callout':
      return (
        <aside key={key} className="callout">
          {block.icon && (
            <span className="callout__icon" aria-hidden="true">
              {block.icon}
            </span>
          )}
          <div>
            <RichText content={block.content} />
          </div>
        </aside>
      );

    case 'image':
      return (
        <figure key={key}>
          <ZoomImage src={block.url} alt={block.caption || ''} />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );

    case 'divider':
      return <hr key={key} />;

    default:
      return null;
  }
}

export default function NotionRenderer({ blocks }) {
  const out = [];
  let list = null; // { type, items }

  const flush = () => {
    if (!list) return;
    const Tag = list.type === 'numbered_list_item' ? 'ol' : 'ul';
    out.push(<Tag key={`list-${out.length}`}>{list.items}</Tag>);
    list = null;
  };

  (blocks || []).forEach((block, i) => {
    const isList = block.type === 'bulleted_list_item' || block.type === 'numbered_list_item';
    if (!isList || (list && list.type !== block.type)) flush();

    if (isList) {
      if (!list) list = { type: block.type, items: [] };
      list.items.push(
        <li key={i}>
          <RichText content={block.content} />
        </li>
      );
      return;
    }

    const el = renderBlock(block, i);
    if (el) out.push(el);
  });
  flush();

  return <article className="prose">{out}</article>;
}
